import React, { ErrorInfo } from 'react';
import { IonApp, isPlatform, setupIonicReact } from '@ionic/react';
import { AutoPylotTheme } from './AutoPylotTheme.tsx';
import { AppContainer, getErrorSafeContainer } from './AppContainer.tsx';
import { AppRouter } from './AppRouter.tsx';
import { ErrorPage } from './ErrorPage.tsx';
import { FatalApplicationErrorState } from './FatalApplicationErrorState.ts';
import { Subscribe } from '@meraki-internal/state';
import { AlertBinder } from './AlertBinder.tsx';
import { ToastBinder } from '../components/toast/ToastBinder.tsx';
import { DialogBinder } from '../components/dialog/DialogBinder.tsx';
import { Logger } from '../support/debug/Logger.ts';
import { Undo } from '../undo/Undo.tsx';
import { BackButtonHandler } from './BackButtonHandler.tsx';
import { DismissSplashScreen } from './DismissSplashScreen.tsx';
import { PaywallTriggerHandler } from '../paywall/PaywallTriggerHandler.tsx';
import { AffiliateLinkModal } from '../affiliates/AffiliateLinkModal.tsx';
import { AircraftFormModal } from '../fleet/AircraftFormModal.tsx';
import { AircraftPicker } from '../fleet/AircraftPicker.tsx';

setupIonicReact({
    swipeBackEnabled: false,

    // required before using html in alert messages
    innerHTMLTemplatesEnabled: true,

    // on web use ios theme, otherwise let ionic decide
    mode: isPlatform('desktop') ? 'ios' : undefined
});

export class AutoPylotApp extends React.Component {
    state = {
        error: null
    };

    static getDerivedStateFromError() {
        // NOTE: We are not setting state here as prescribed in the docs, but instead setting it in
        // componentDidCatch() below because a non-static method can check if error state is already
        // set, and avoid the real error getting overwritten by a bogus error when react remounts
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        try {
            // ignore if we get catch a second error - this is from react unsuccessfully attempting
            // to remount the app, and contains a misleading message and useless stack trace
            if (this.state.error) {
                return;
            }

            const log = getErrorSafeContainer().get(Logger);

            log.error(error, errorInfo);
            this.setState({ error });

        } catch (e: any) {
            console.warn(`Failed to log error in AutoPylotApp.componentDidCatch. ${e.message}`);
            console.error(e);
        }
    }

    render() {
        const { error } = this.state;

        if (error) {
            return <ErrorPage error={error} />;
        }

        return (
            <Subscribe to={() => FatalApplicationErrorState.singleton}>
                {({ error: fatalError }) => (
                    <>
                        {fatalError && (
                            <DismissSplashScreen>
                                <ErrorPage error={fatalError} />
                            </DismissSplashScreen>
                        )}
                        {!fatalError && (
                            <IonApp>
                                <AutoPylotTheme />
                                <AlertBinder />
                                <AppContainer>
                                    <BackButtonHandler />
                                    <Undo />
                                    <ToastBinder />
                                    <DialogBinder />
                                    <PaywallTriggerHandler />
                                    <AppRouter />
                                    <AffiliateLinkModal />
                                    <AircraftFormModal />
                                    <AircraftPicker />
                                </AppContainer >
                            </IonApp>
                        )}
                    </>
                )}
            </Subscribe>
        );
    }
}
